import { Box, Stack, Typography } from '@mui/material'
import { Fragment, ReactNode } from 'react'

const NOISE_KEYS = new Set([
  'isCheck',
  'isCustom',
  'order',
  'toolCallId',
  'etReferenceId',
  'syncToMobileApp',
])

// App-wide convention (source of truth: WateringWindow/index.tsx):
// 2=Mon, 3=Tue, 4=Wed, 5=Thu, 6=Fri, 7=Sat, 8=Sun. 1 is unused.
const WEEKDAY_MAP: Record<number, string> = {
  2: 'Mon',
  3: 'Tue',
  4: 'Wed',
  5: 'Thu',
  6: 'Fri',
  7: 'Sat',
  8: 'Sun',
}

const MONTH_NAMES = [
  '',
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

const PERIOD_KEYS = new Set([
  'fromMonth',
  'fromDay',
  'toMonth',
  'toDay',
  'fromHour',
  'fromMinute',
  'toHour',
  'toMinute',
  'isWholeYearPeriod',
])

const humanizeKey = (key: string): string =>
  key
    .replace(/([A-Z]+)/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .replace(/\s+/g, ' ')
    .trim()

const formatPrimitive = (value: unknown): string => {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number') return String(value)
  if (typeof value === 'string') return value.length === 0 ? '—' : value
  return String(value)
}

const formatWeekDays = (value: unknown[]): string | null => {
  if (!value.every((v) => typeof v === 'number' && WEEKDAY_MAP[v as number]))
    return null
  const names = (value as number[]).map((d) => WEEKDAY_MAP[d])
  return names.length > 0 ? names.join(', ') : null
}

const isPrimitive = (value: unknown): boolean =>
  value === null || value === undefined || typeof value !== 'object'

const isPrimitiveLike = (value: unknown): boolean => {
  if (isPrimitive(value)) return true
  if (Array.isArray(value)) {
    if (value.length === 0) return true
    if (value.every(isPrimitive)) return true
  }
  return false
}

const pad2 = (n: number) => String(n).padStart(2, '0')

interface PeriodSummary {
  label: string
  value: string
}

const formatPeriod = (
  obj: Record<string, unknown>,
): PeriodSummary | null => {
  const hasDate =
    typeof obj.fromMonth === 'number' &&
    typeof obj.fromDay === 'number' &&
    typeof obj.toMonth === 'number' &&
    typeof obj.toDay === 'number'
  const hasTime =
    typeof obj.fromHour === 'number' &&
    typeof obj.fromMinute === 'number' &&
    typeof obj.toHour === 'number' &&
    typeof obj.toMinute === 'number'
  if (!hasDate && !hasTime) return null

  const parts: string[] = []
  if (hasDate) {
    if (obj.isWholeYearPeriod === true) {
      parts.push('whole year')
    } else {
      const fm = MONTH_NAMES[obj.fromMonth as number] ?? obj.fromMonth
      const tm = MONTH_NAMES[obj.toMonth as number] ?? obj.toMonth
      parts.push(`${fm} ${obj.fromDay} — ${tm} ${obj.toDay}`)
    }
  }
  if (hasTime) {
    parts.push(
      `${pad2(obj.fromHour as number)}:${pad2(obj.fromMinute as number)} — ${pad2(obj.toHour as number)}:${pad2(obj.toMinute as number)}`,
    )
  }
  return { label: hasDate ? 'Period' : 'Time', value: parts.join(', ') }
}

const labelSx = {
  fontSize: 12,
  fontWeight: 500,
  color: 'grey.800',
  whiteSpace: 'nowrap',
  pr: 1,
}

const valueSx = {
  fontSize: 12,
  color: 'text.main',
  wordBreak: 'break-word',
}

const sectionLabelSx = {
  fontSize: 11.5,
  fontWeight: 600,
  color: 'text.main',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  mb: 0.5,
}

const emptySx = {
  ...valueSx,
  color: 'grey.600',
  fontStyle: 'italic',
}

const NestedCard = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'border.devider',
      borderRadius: 1,
      px: 1.25,
      py: 1,
    }}
  >
    {children}
  </Box>
)

const renderPrimitiveValue = (value: unknown, parentKey?: string): ReactNode => {
  if (isPrimitive(value)) {
    return (
      <Typography component="span" sx={valueSx}>
        {formatPrimitive(value)}
      </Typography>
    )
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <Typography component="span" sx={emptySx}>
          empty
        </Typography>
      )
    }
    if (parentKey === 'weekDays') {
      const formatted = formatWeekDays(value)
      if (formatted) {
        return (
          <Typography component="span" sx={valueSx}>
            {formatted}
          </Typography>
        )
      }
    }
    return (
      <Typography component="span" sx={valueSx}>
        {value.map(formatPrimitive).join(', ')}
      </Typography>
    )
  }
  return null
}

interface ActionArgsTableProps {
  data: unknown
  parentKey?: string
}

const ActionArgsTable = ({ data, parentKey }: ActionArgsTableProps) => {
  if (isPrimitive(data) || Array.isArray(data)) {
    return <>{renderPrimitiveValue(data, parentKey)}</>
  }

  const obj = data as Record<string, unknown>
  const periodSummary = formatPeriod(obj)

  const entries = Object.entries(obj).filter(([k, v]) => {
    if (NOISE_KEYS.has(k)) return false
    if (v === null || v === undefined) return false
    if (periodSummary && PERIOD_KEYS.has(k)) return false
    return true
  })

  const primitiveEntries = entries.filter(([, v]) => isPrimitiveLike(v))
  const complexEntries = entries.filter(([, v]) => !isPrimitiveLike(v))

  return (
    <Stack spacing={1.25}>
      {(primitiveEntries.length > 0 || periodSummary) && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(110px, max-content) 1fr',
            columnGap: 1.5,
            rowGap: 0.6,
            alignItems: 'baseline',
          }}
        >
          {periodSummary && (
            <Fragment>
              <Typography sx={labelSx}>{periodSummary.label}</Typography>
              <Typography sx={valueSx}>{periodSummary.value}</Typography>
            </Fragment>
          )}
          {primitiveEntries.map(([key, value]) => (
            <Fragment key={key}>
              <Typography sx={labelSx}>{humanizeKey(key)}</Typography>
              <Box sx={{ minWidth: 0 }}>
                {renderPrimitiveValue(value, key)}
              </Box>
            </Fragment>
          ))}
        </Box>
      )}
      {complexEntries.map(([key, value]) => (
        <Box key={key}>
          <Typography sx={sectionLabelSx}>{humanizeKey(key)}</Typography>
          {Array.isArray(value) ? (
            <Stack spacing={0.75}>
              {value.map((item, idx) => (
                <NestedCard key={idx}>
                  <ActionArgsTable data={item} parentKey={key} />
                </NestedCard>
              ))}
            </Stack>
          ) : (
            <NestedCard>
              <ActionArgsTable data={value} parentKey={key} />
            </NestedCard>
          )}
        </Box>
      ))}
    </Stack>
  )
}

export default ActionArgsTable
